/**
 * Position model for the free-plan Autoclipper watermark: the CENTER of the
 * watermark box as fractions of the frame. The box is 9% of the frame height
 * with a 3:1 aspect (the 900x300 design).
 *
 * Mirrors autoclipper-backend src/lib/watermarkPosition.js; both sides run the
 * shared watermarkPositionTestVectors.json fixture. The backend re-clamps on
 * every export, so this clamp is for the preview/drag only.
 */
export type WatermarkPosition = { x: number; y: number };

export const DEFAULT_WATERMARK_POSITION: WatermarkPosition = Object.freeze({ x: 0.5, y: 0.795 });
export const WATERMARK_HEIGHT_FRACTION = 0.09;
export const WATERMARK_DESIGN_WIDTH = 900;
export const WATERMARK_DESIGN_HEIGHT = 300;
const WATERMARK_ASPECT = WATERMARK_DESIGN_WIDTH / WATERMARK_DESIGN_HEIGHT;

const SAFE_ZONES = {
  portrait: { left: 0.04, right: 0.86, top: 0.1, bottom: 0.86 },
  landscape: { left: 0.04, right: 0.96, top: 0.06, bottom: 0.94 },
} as const;

const isFiniteNumber = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const round4 = (v: number) => Math.round(v * 10000) / 10000;
const normalizeAspect = (aspect: unknown) => (isFiniteNumber(aspect) && aspect > 0 ? aspect : 9 / 16);

export function sanitizeWatermarkPosition(value: unknown): WatermarkPosition | null {
  if (!value || typeof value !== 'object') return null;
  const { x, y } = value as { x?: unknown; y?: unknown };
  if (!isFiniteNumber(x) || !isFiniteNumber(y)) return null;
  return { x: Math.min(1, Math.max(0, x)), y: Math.min(1, Math.max(0, y)) };
}

/** Watermark box size as frame fractions for a frame of `frameAspect` (w/h). */
export function watermarkBoxSize(frameAspect: number): { w: number; h: number } {
  const h = WATERMARK_HEIGHT_FRACTION;
  return { w: round4((WATERMARK_ASPECT * h) / normalizeAspect(frameAspect)), h };
}

export function clampWatermarkPosition(position: unknown, frameAspect: number): WatermarkPosition {
  const p = sanitizeWatermarkPosition(position) ?? DEFAULT_WATERMARK_POSITION;
  const aspect = normalizeAspect(frameAspect);
  const zone = aspect < 1 ? SAFE_ZONES.portrait : SAFE_ZONES.landscape;
  const h = WATERMARK_HEIGHT_FRACTION;
  const w = (WATERMARK_ASPECT * h) / aspect;
  const clamp = (v: number, lo: number, hi: number) => (lo > hi ? (lo + hi) / 2 : Math.min(hi, Math.max(lo, v)));
  return {
    x: round4(clamp(p.x, zone.left + w / 2, zone.right - w / 2)),
    y: round4(clamp(p.y, zone.top + h / 2, zone.bottom - h / 2)),
  };
}
